import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import { CreditCardIcon, XMarkIcon } from '@heroicons/react/24/outline';
import LoadingSpinner from '../ui/LoadingSpinner';
import { paymentTrackingService } from '../../services/financial/payment-tracking';
import toast from 'react-hot-toast';

const RecordPaymentModal = ({ invoice, onClose, onSuccess }) => {
  const { t } = useTranslation();
  const { currentCompany } = useAuth();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    amount: Math.round((invoice.totalAmount - (invoice.paidAmount || 0)) * 100) / 100,
    paymentDate: new Date().toISOString().split('T')[0],
    paymentMethod: 'bank_transfer',
    reference: '',
    notes: '',
    partialPayment: false
  });
  const [errors, setErrors] = useState({});

  const paymentMethods = [
    { id: 'bank_transfer', name: t('accounting.bank_transfer') },
    { id: 'cash', name: t('accounting.cash') },
    { id: 'check', name: t('accounting.check') },
    { id: 'credit_card', name: t('expenses.payment.card') },
    { id: 'other', name: t('accounting.expenseCategories.other') }
  ];

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Clear error for this field
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: null
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.amount || formData.amount <= 0) {
      newErrors.amount = t('payment.error.amount_required', 'Payment amount is required');
    }
    
    const remainingAmount = invoice.totalAmount - (invoice.paidAmount || 0);
    if (formData.amount > remainingAmount) {
      newErrors.amount = t('payment.error.amount_exceeds', 'Amount cannot exceed remaining balance');
    }
    
    if (!formData.paymentDate) {
      newErrors.paymentDate = t('payment.error.date_required', 'Payment date is required');
    }
    
    if (!formData.paymentMethod) {
      newErrors.paymentMethod = t('payment.error.method_required', 'Payment method is required');
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;
    
    setLoading(true);
    
    try {
      // Record the payment
      const paymentData = {
        invoiceId: invoice.id,
        clientId: invoice.clientId,
        customerName: invoice.clientName || 'Unknown Customer',
        amount: parseFloat(formData.amount),
        paymentDate: formData.paymentDate,
        paymentMethod: formData.paymentMethod,
        reference: formData.reference || null,
        notes: formData.notes || null,
        companyId: currentCompany.id,
        createdBy: currentCompany.id,
        createdAt: new Date().toISOString()
      };
      
      console.log('Recording payment with data:', paymentData);
      
      // Record the payment (this automatically updates the invoice status)
      const paymentId = await paymentTrackingService.recordPayment(paymentData);
      console.log('Payment recorded successfully with ID:', paymentId);
      
      toast.success(t('payment.success.recorded', 'Payment recorded successfully'));
      onSuccess();
      
    } catch (error) {
      console.error('Detailed error recording payment:', error);
      console.error('Error code:', error.code);
      console.error('Error message:', error.message);
      
      // Provide more specific error messages
      let errorMessage = t('payment.error.recording_failed', 'Failed to record payment');
      
      if (error.code === 'permission-denied') {
        errorMessage = t('payment.error.permission_denied', 'Permission denied. Please check your access rights.');
      } else if (error.code === 'unavailable') {
        errorMessage = t('payment.error.service_unavailable', 'Payment service temporarily unavailable. Please try again.');
      } else if (error.code === 'failed-precondition') {
        errorMessage = t('payment.error.database_index_required', 'Database configuration error. Please contact support.');
      } else if (error.message && error.message.includes('index')) {
        errorMessage = t('payment.error.database_index_required', 'Database configuration error. Please contact support.');
      }
      
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4 p-6">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-semibold text-gray-900">
            {t('payment.record_payment', 'Record Payment')}
          </h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        {/* Invoice Details */}
        <div className="bg-gray-50 rounded-lg p-4 mb-4">
          <h4 className="font-medium text-gray-900 mb-2">
            {t('payment.invoice_details', 'Invoice Details')}
          </h4>
          <div className="space-y-1 text-sm">
            <div className="flex justify-between">
              <span className="text-gray-600">{t('payment.invoice_number', 'Invoice')}:</span>
              <span className="font-medium">{invoice.id}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">{t('payment.client', 'Client')}:</span>
              <span className="font-medium">{invoice.clientName}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">{t('payment.total_amount', 'Total Amount')}:</span>
              <span className="font-medium">SAR {invoice.totalAmount.toLocaleString('en-US')}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">{t('payment.paid_amount', 'Paid Amount')}:</span>
              <span className="font-medium">SAR {(invoice.paidAmount || 0).toLocaleString('en-US')}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">{t('payment.outstanding', 'Outstanding')}:</span>
              <span className="font-medium text-red-600">SAR {(invoice.totalAmount - (invoice.paidAmount || 0)).toLocaleString('en-US')}</span>
            </div>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Payment Amount */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('payment.amount', 'Payment Amount')} <span className="text-red-500">*</span>
            </label>
            <input
              type="number"
              step="0.01"
              min="0"
              max={invoice.totalAmount - (invoice.paidAmount || 0)}
              value={formData.amount}
              onChange={(e) => handleInputChange('amount', e.target.value)}
              className={`input input-bordered w-full ${errors.amount ? 'border-red-500' : ''}`}
              placeholder={t('payment.amount_placeholder', 'Enter payment amount')}
            />
            {errors.amount && <p className="text-red-500 text-sm mt-1">{errors.amount}</p>}
          </div>

          {/* Payment Date */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('payment.date', 'Payment Date')} <span className="text-red-500">*</span>
            </label>
            <input
              type="date"
              value={formData.paymentDate}
              onChange={(e) => handleInputChange('paymentDate', e.target.value)}
              className={`input input-bordered w-full ${errors.paymentDate ? 'border-red-500' : ''}`}
            />
            {errors.paymentDate && <p className="text-red-500 text-sm mt-1">{errors.paymentDate}</p>}
          </div>

          {/* Payment Method */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('payment.method', 'Payment Method')} <span className="text-red-500">*</span>
            </label>
            <select
              value={formData.paymentMethod}
              onChange={(e) => handleInputChange('paymentMethod', e.target.value)}
              className={`select select-bordered w-full ${errors.paymentMethod ? 'border-red-500' : ''}`}
            >
              {paymentMethods.map(method => (
                <option key={method.id} value={method.id}>
                  {method.name}
                </option>
              ))}
            </select>
            {errors.paymentMethod && <p className="text-red-500 text-sm mt-1">{errors.paymentMethod}</p>}
          </div>

          {/* Reference */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('payment.reference', 'Reference')}
            </label>
            <input
              type="text"
              value={formData.reference}
              onChange={(e) => handleInputChange('reference', e.target.value)}
              className="input input-bordered w-full"
              placeholder={t('payment.reference_placeholder', 'Transaction reference or check number')}
            />
          </div>

          {/* Notes */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('payment.notes', t('common.notes'))}
            </label>
            <textarea
              value={formData.notes}
              onChange={(e) => handleInputChange('notes', e.target.value)}
              rows="3"
              className="textarea textarea-bordered w-full"
              placeholder={t('payment.notes_placeholder', 'Additional notes about the payment')}
            />
          </div>

          {/* Action Buttons */}
          <div className="flex justify-end space-x-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="btn btn-outline"
              disabled={loading}
            >
              {t('common.cancel', t('common.cancel'))}
            </button>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={loading}
            >
              {loading ? (
                <>
                  <LoadingSpinner size="sm" />
                  {t('payment.recording', 'Recording...')}
                </>
              ) : (
                <>
                  <CreditCardIcon className="h-4 w-4 mr-2" />
                  {t('payment.record', 'Record Payment')}
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default RecordPaymentModal; 